/**
 * @fileoverview HTTP请求工具
 * @description 提供axios封装的请求方法，自动处理token和街道信息，解决CORS问题
 * @author wangzhongsheng
 * @version 2.0.0
 */

import axios from 'axios'
import { detectStreetKey } from './street.js'
import { getSDKConfig, addConfigListener } from './config.js'

/**
 * 创建请求实例（内部使用）
 * @returns {Object} axios实例
 */
function createRequest() {
  // 获取全局配置
  const globalConfig = getSDKConfig()
  

  const instance = axios.create({
    baseURL: globalConfig.baseURL,
    timeout: globalConfig.timeout,
    headers: {
      'Content-Type': 'application/json'
    }
  })

  // 请求拦截器
  instance.interceptors.request.use(
    (config) => {
      // 自动添加token
      const token = localStorage.getItem(globalConfig.tokenKey)
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }

      // 自动添加当前街道信息（如果启用）
      if (globalConfig.enableAutoDetectStreet) {
        const currentStreet = detectStreetKey()
        if (currentStreet) {
          config.headers['X-Street-Key'] = currentStreet
        }
      }

      return config
    },
    (error) => Promise.reject(error)
  )

  // 响应拦截器
  instance.interceptors.response.use(
    (response) => {
      // 检测token失效响应 - 支持多种token相关错误
      const responseData = response.data
      
      if (responseData && 
          responseData.success === false && 
          responseData.code && 
          globalConfig.tokenErrorCodes.includes(responseData.code)) {
        
        // token失效，清除本地存储
        localStorage.removeItem(globalConfig.tokenKey)
        localStorage.removeItem(globalConfig.userKey)
        
        // 触发token过期事件
        window.dispatchEvent(new CustomEvent('bjchy-token-expired', {
          detail: { 
            response: responseData, 
            streetKey: detectStreetKey() 
          }
        }))
        
        // 自动跳转到登录页面
        const loginUrl = globalConfig.loginUrl || '/'
        if (typeof window !== 'undefined' && window.location) {
          console.log(`🔄 Token失效(${responseData.code})，自动跳转到:`, loginUrl)
          console.log('错误信息:', responseData.error || responseData.message)
        }
        
        return Promise.reject(new Error(responseData.error || responseData.message || 'Token失效'))
      }
      
      // 处理token更新（如果启用）
      if (globalConfig.enableTokenAutoRefresh) {
        const newToken = response.headers['x-new-token']
        if (newToken) {
          localStorage.setItem(globalConfig.tokenKey, newToken)
        }
      }
      return response.data
    },
    (error) => {
      // 统一错误处理
      if (error.response?.status === 401) {
        // HTTP 401状态码处理（兼容旧的token失效处理方式）
        localStorage.removeItem(globalConfig.tokenKey)
        localStorage.removeItem(globalConfig.userKey)
        
        // 触发token过期事件
        window.dispatchEvent(new CustomEvent('bjchy-token-expired', {
          detail: { error, streetKey: detectStreetKey() }
        }))
        
        // 自动跳转到登录页面
        const loginUrl = globalConfig.loginUrl || '/'
        if (typeof window !== 'undefined' && window.location) {
          console.log('🔄 Token过期(401)，自动跳转到:', loginUrl)
          window.location.href = loginUrl
        }
      }
      
      return Promise.reject(error)
    }
  )

  // 为实例添加便捷方法
  instance.get = (url, params = {}) => instance({ method: 'GET', url, params })
  instance.post = (url, data = {}) => instance({ method: 'POST', url, data })
  instance.put = (url, data = {}) => instance({ method: 'PUT', url, data })
  instance.delete = (url, params = {}) => instance({ method: 'DELETE', url, params })

  return instance
}

/**
 * 默认请求实例 - 自动使用全局配置
 */
let defaultRequestInstance = createRequest()

/**
 * 当全局配置变更时，重新创建默认请求实例
 */
addConfigListener((newConfig, oldConfig) => {
  // 只有当影响请求的配置发生变化时才重新创建实例
  const shouldRecreate = 
    newConfig.baseURL !== oldConfig.baseURL ||
    newConfig.timeout !== oldConfig.timeout ||
    newConfig.tokenKey !== oldConfig.tokenKey ||
    newConfig.enableAutoDetectStreet !== oldConfig.enableAutoDetectStreet ||
    newConfig.enableTokenAutoRefresh !== oldConfig.enableTokenAutoRefresh ||
    newConfig.loginUrl !== oldConfig.loginUrl ||
    JSON.stringify(newConfig.tokenErrorCodes) !== JSON.stringify(oldConfig.tokenErrorCodes)

  if (shouldRecreate) {
    console.log('🔄 全局配置变更，重新创建默认请求实例')
    defaultRequestInstance = createRequest()
    
    // 更新导出的request对象的方法
    request.get = defaultRequestInstance.get
    request.post = defaultRequestInstance.post  
    request.put = defaultRequestInstance.put
    request.delete = defaultRequestInstance.delete
    
    // 更新request函数本身
    Object.setPrototypeOf(request, defaultRequestInstance)
    Object.assign(request, defaultRequestInstance)
  }
})

/**
 * 默认请求对象 - 会随全局配置自动更新
 */
export const request = new Proxy(defaultRequestInstance, {
  get(target, prop) {
    // 总是从当前的默认实例获取属性和方法
    return defaultRequestInstance[prop]
  },
  apply(target, thisArg, argumentsList) {
    // 当直接调用request()时，使用当前的默认实例
    return defaultRequestInstance.apply(thisArg, argumentsList)
  }
})


